import { createSelector } from '@reduxjs/toolkit';
import type { RootState } from './index';

// ── User selectors ──────────────────────────────────────────────────
export const selectUserId = (state: RootState) => state.user.uid;
export const selectUserStatus = (state: RootState) => state.user.status;
export const selectUserEmail = (state: RootState) => state.user.email;
// Memoized — returning a fresh object on every call caused "selector returned
// a different result with the same parameters" warnings and extra rerenders
export const selectUserName = createSelector(
  [(state: RootState) => state.user.given_name, (state: RootState) => state.user.family_name],
  (givenName, familyName) => ({ givenName, familyName }),
);
export const selectUserImage = (state: RootState) => state.user.image;
export const selectUserIdentityId = (state: RootState) => state.user.identityId;
export const selectOldUserDetails = (state: RootState) => state.user.oldUserDetails;

// ── Workspace selectors ─────────────────────────────────────────────
export const selectCurrentGroupId = (state: RootState) => state.workspace.currentUserGroup;
export const selectAllWorkspaces = (state: RootState) => state.workspace.allWorkspaces;
export const selectActiveWorkspace = (state: RootState) => {
  const workspaces = state.workspace.allWorkspaces;
  const groupId = state.workspace.currentUserGroup;
  return workspaces.find((w) => w.id === groupId) ?? null;
};
export const selectWorkspaceUsers = (state: RootState) => state.workspace.workspaceUsers;
export const selectInvitedWorkspaceUsers = (state: RootState) => state.workspace.invitedUsers;
export const selectRootGroup = (state: RootState) => state.workspace.rootGroup;
export const selectCreatedGroup = (state: RootState) => state.workspace.createdGroup;
export const selectPackages = (state: RootState) => state.workspace.packages;
export const selectSelectedPackage = (state: RootState) => state.workspace.selectedPackage;

// ── Project selectors ───────────────────────────────────────────────
export const selectProjectId = (state: RootState) => state.project.projectId;
export const selectProjectTitle = (state: RootState) => state.project.title;
export const selectProjectItems = (state: RootState) => state.project.items;
export const selectProjectTasks = (state: RootState) => state.project.tasks;
export const selectProjectMessages = (state: RootState) => state.project.messages;
export const selectProjectUsers = (state: RootState) => state.project.users;
export const selectProjectInvitedUsers = (state: RootState) => state.project.invitedUsers;
export const selectSelectedItem = (state: RootState) => state.project.selectedItem;
export const selectUserRole = (state: RootState) => state.project.userRole;
export const selectProjectDetails = (state: RootState) => state.project.details;
export const selectProjectHeader = (state: RootState) => state.project.header;
export const selectProjectLoading = (state: RootState) => state.project.loading;
export const selectProjectError = (state: RootState) => state.project.error;
export const selectProjectModalOpen = (state: RootState) => state.project.modalOpen;
export const selectGuestHash = (state: RootState) => state.project.guestHash;

// ── Items cache selectors ──────────────────────────────────────────
export { selectItemsForParent, selectParentMeta, selectCacheAge } from './itemsSlice';
